<template>
    <section>
        <div v-if="mode === 'existing'">
            <q-banner
                v-if="selectedPerson && comparisonRows.length"
                class="bg-blue-1 text-blue-10"
                rounded
            >
                Referral demographics are shown for comparison only. Existing person details will not be changed.
            </q-banner>
            <div v-if="selectedPerson && comparisonRows.length" class="comparison-list q-mt-md">
                <div
                    v-for="row in comparisonRows"
                    :key="row.key"
                    class="comparison-row"
                >
                    <div>
                        <strong>{{ row.label }}</strong>
                        <span>Stored: {{ row.stored }}</span>
                    </div>
                    <div>
                        <q-chip
                            dense
                            square
                            :color="row.differs ? 'orange-2' : 'green-2'"
                            :text-color="row.differs ? 'orange-10' : 'green-10'"
                            :icon="row.differs ? 'difference' : 'check'"
                        >
                            {{ row.differs ? "Different" : "Matches" }}
                        </q-chip>
                        <span>Referral: {{ row.extracted }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="q-mt-lg">
            <q-banner v-if="aiPersonFields.length" class="bg-blue-1 text-blue-10 q-mb-md" rounded>
                <q-icon name="auto_awesome" class="q-mr-xs" />
                The referral filled in {{ aiPersonFields.length }}
                person field{{ aiPersonFields.length === 1 ? "" : "s" }}.
                Check each value and make any changes needed.
            </q-banner>
            <TkPersonFields
                :model-value="personDraft"
                :dense="true"
                :start-expanded="true"
                :require-name="true"
                @update:model-value="value => $emit('update:personDraft', value)"
            />
        </div>
    </section>
</template>

<script>
import TkPersonFields from "@/components/ClientComponents/TemporaryClients/TkPersonFields.vue";
import {
    displayReferralValue,
    mappedPersonFields,
    valuesDiffer,
} from "@/services/util/referralWorkflow";
import { toDDMMYYYY } from "@/services/util/tkClients";

export default {
    name: "ReferralPersonSection",
    components: { TkPersonFields },
    emits: ["update:personDraft"],
    props: {
        mode: {
            type: String,
            required: true,
        },
        selectedPerson: {
            type: [Object, null],
            required: false,
        },
        personDraft: {
            type: Object,
            required: true,
        },
        extractionFields: {
            type: Array,
            required: true,
        },
    },
    computed: {
        aiPersonFields() {
            return mappedPersonFields(this.extractionFields);
        },
        comparisonRows() {
            if (!this.selectedPerson) return [];
            return this.aiPersonFields.map(field => {
                const fieldName = field.destination.field;
                const storedValue = fieldName === "date_of_birth"
                    ? toDDMMYYYY(this.selectedPerson[fieldName])
                    : this.selectedPerson[fieldName];
                return {
                    key: field.originalFieldKey,
                    label: field.label,
                    stored: displayReferralValue(storedValue),
                    extracted: displayReferralValue(field.reviewedValue),
                    differs: valuesDiffer(
                        this.selectedPerson[fieldName],
                        field.reviewedValue,
                        fieldName
                    ),
                };
            });
        },
    },
};
</script>

<style scoped>
.comparison-list {
    border: 1px solid rgba(47, 62, 70, 0.12);
    border-radius: 6px;
}

.comparison-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    padding: 12px;
    border-bottom: 1px solid rgba(47, 62, 70, 0.08);
}

.comparison-row:last-child {
    border-bottom: 0;
}

.comparison-row > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
}

.comparison-row span {
    color: #5d6b70;
    overflow-wrap: anywhere;
}

@media (max-width: 700px) {
    .comparison-row {
        grid-template-columns: 1fr;
    }
}
</style>
